import { useState, useEffect, useCallback } from 'react'
import { Input, Table, Button, Spin, Pagination } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { SearchOutlined, DatabaseOutlined, FileTextOutlined, ArrowRightOutlined, ReloadOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { getStockKBList, getStockKBStats } from '@/api/stock'
import type { StockKBItem, StockKBStats } from '@/api/stock'
import { mockStockList } from './mockData'
import './index.scss'

const reportTypeLabel: Record<string, string> = {
    annual: '年报', interim_h1: '中报(H1)', interim_h2: '中报(H2)',
    quarterly_q1: 'Q1', quarterly_q2: 'Q2', quarterly_q3: 'Q3', quarterly_q4: 'Q4',
    research: '研报',
}

function fmtMktCap(v: number | null) {
    if (!v) return '—'
    if (v >= 1e12) return (v / 1e12).toFixed(2) + '万亿'
    if (v >= 1e8) return (v / 1e8).toFixed(0) + '亿'
    return v.toLocaleString()
}

function PctCell({ v }: { v: number | null }) {
    if (v == null) return <span style={{ color: 'var(--text-3)' }}>—</span>
    const sign = v >= 0 ? '+' : ''
    return <span className={v >= 0 ? 'pos' : 'neg'}>{sign}{v.toFixed(1)}%</span>
}

const marketLabel: Record<string, string> = { hk: '港股', us: '美股', a: 'A股', other: '其他' }

const VALID_CODE_RE = /^(HK|US|SH|SZ)\.[A-Z0-9]+$/i
function filterValidItems(items: StockKBItem[]) {
    return items.filter(item => VALID_CODE_RE.test(item.stock_code))
}

// Mock fallback for stats panel
const mockStats: StockKBStats = {
    total_stocks: mockStockList.length,
    total_reports: mockStockList.reduce((s, st) => s + ((st as any).indicators?.length ?? 0), 0),
    market_breakdown: { hk: mockStockList.filter(s => s.market === 'hk').length },
    report_type_breakdown: { annual: 4, interim_h1: 3, research: 2 },
    recent_reports: mockStockList.slice(0, 5).map((s, i) => ({
        id: i + 1,
        stock_code: s.code,
        name: s.name,
        fiscal_year: (s as any).indicators?.[0]?.fiscal_year ?? '2023',
        report_type: 'annual',
        report_date: s.latest_report_date,
        currency: s.currency,
    })),
}

// Mock fallback list items
const mockAsApiItems: StockKBItem[] = mockStockList.map(s => ({
    stock_code: s.code,
    name: s.name,
    fiscal_year: (s as any).indicators?.[0]?.fiscal_year ?? null,
    report_type: null,
    report_date: s.latest_report_date,
    closing_price: s.closing_price,
    market_cap: s.market_cap,
    pe_ttm: s.pe_ttm,
    pb_ratio: s.pb_ratio,
    revenue_yoy_pct: s.revenue_yoy_pct,
    adjusted_net_profit_yoy_pct: s.net_profit_yoy_pct,
    currency: s.currency,
    market: s.market,
}))

// ── Sub-components ──────────────────────────────────────────────

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string | number; sub?: string }) {
    return (
        <div className="skb__stat-card">
            <div className="skb__stat-icon">{icon}</div>
            <div className="skb__stat-body">
                <div className="skb__stat-top">
                    <span className="skb__stat-value">{value}</span>
                    {sub && <span className="skb__stat-sub">{sub}</span>}
                </div>
                <div className="skb__stat-label">{label}</div>
            </div>
        </div>
    )
}

function MarketTile({ market, count, onClick }: { market: string; count: number; onClick: () => void }) {
    const labels: Record<string, string> = { hk: '港股', us: '美股', a: 'A股', other: '其他' }
    return (
        <div className={`skb__mkt-tile skb__mkt-tile--${market}`} onClick={onClick}>
            <div className="skb__mkt-tile-name">{labels[market] ?? market}</div>
            <div className="skb__mkt-tile-count">{count} 只</div>
            <ArrowRightOutlined className="skb__mkt-tile-arrow" />
        </div>
    )
}

function RecentReportRow({ r, onClick }: { r: StockKBStats['recent_reports'][0]; onClick: () => void }) {
    const typeLabel = reportTypeLabel[r.report_type ?? ''] ?? r.report_type ?? '报告'
    return (
        <div className="skb__recent-row" onClick={onClick}>
            <div className="skb__recent-left">
                <span className="skb__recent-code">{r.stock_code}</span>
                <span className="skb__recent-name">{r.name ?? '—'}</span>
            </div>
            <div className="skb__recent-center">
                <span className="skb__recent-period">{r.fiscal_year ?? '—'}</span>
                <span className="skb__recent-type">{typeLabel}</span>
            </div>
            <div className="skb__recent-date">{r.report_date ?? '—'}</div>
        </div>
    )
}

function getColumns(navigate: (p: string) => void): ColumnsType<StockKBItem> {
    return [
        {
            title: '股票', key: 'stock',
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 700, color: '#fff', fontSize: 13 }}>{r.name ?? r.stock_code}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{r.stock_code}</div>
                </div>
            ),
        },
        {
            title: '市场', dataIndex: 'market', key: 'market',
            render: (v: string) => (
                <span className={`skb__market-tag skb__market-tag--${v}`}>{marketLabel[v] ?? v}</span>
            ),
        },
        {
            title: '最新价', key: 'price',
            render: (_, r) => r.closing_price
                ? <span style={{ color: '#fff', fontWeight: 600 }}>
                    {r.currency === 'HKD' ? 'HK$' : r.currency === 'USD' ? '$' : '¥'}{r.closing_price.toFixed(2)}
                  </span>
                : <span style={{ color: 'var(--text-3)' }}>—</span>,
        },
        {
            title: '市值', dataIndex: 'market_cap', key: 'mktcap',
            render: (v: number | null) => <span style={{ color: 'var(--text-2)' }}>{fmtMktCap(v)}</span>,
            sorter: (a, b) => (a.market_cap ?? 0) - (b.market_cap ?? 0),
        },
        {
            title: 'PE(TTM)', dataIndex: 'pe_ttm', key: 'pe',
            render: (v: number | null) => v && v > 0
                ? <span style={{ color: 'var(--text-2)' }}>{v.toFixed(1)}x</span>
                : <span style={{ color: 'var(--text-3)' }}>—</span>,
            sorter: (a, b) => (a.pe_ttm ?? 0) - (b.pe_ttm ?? 0),
        },
        {
            title: 'PB', dataIndex: 'pb_ratio', key: 'pb',
            render: (v: number | null) => v
                ? <span style={{ color: 'var(--text-2)' }}>{v.toFixed(2)}x</span>
                : <span style={{ color: 'var(--text-3)' }}>—</span>,
            sorter: (a, b) => (a.pb_ratio ?? 0) - (b.pb_ratio ?? 0),
        },
        {
            title: '收入YoY', dataIndex: 'revenue_yoy_pct', key: 'rev_yoy',
            render: (v: number | null) => <PctCell v={v} />,
            sorter: (a, b) => (a.revenue_yoy_pct ?? 0) - (b.revenue_yoy_pct ?? 0),
        },
        {
            title: '利润YoY', dataIndex: 'adjusted_net_profit_yoy_pct', key: 'profit_yoy',
            render: (v: number | null) => <PctCell v={v} />,
            sorter: (a, b) => (a.adjusted_net_profit_yoy_pct ?? 0) - (b.adjusted_net_profit_yoy_pct ?? 0),
        },
        {
            title: '最新报告', dataIndex: 'report_date', key: 'report_date',
            render: (v: string | null) => <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{v ?? '—'}</span>,
        },
        {
            title: '', key: 'action',
            render: (_, r) => (
                <Button size="small" type="link" onClick={e => { e.stopPropagation(); navigate(`/stock-kb/${encodeURIComponent(r.stock_code)}`) }}>
                    查看 →
                </Button>
            ),
        },
    ]
}

// ── Dashboard ────────────────────────────────────────────────────

function Dashboard({
    stats,
    usingMock,
    onSearch,
    onMarket,
    navigate,
}: {
    stats: StockKBStats | null
    usingMock: boolean
    onSearch: (q: string) => void
    onMarket: (m: string) => void
    navigate: (p: string) => void
}) {
    const [q, setQ] = useState('')
    const mkt = stats?.market_breakdown ?? {}
    const totalStocks = stats?.total_stocks ?? 0
    const totalReports = stats?.total_reports ?? 0

    // 动态计算各市场入口：hk/us/a 单独展示，其余合并为"其他"
    const CORE_KEYS = ['hk', 'us', 'a']
    const otherCount = Object.entries(mkt)
        .filter(([k]) => !CORE_KEYS.includes(k))
        .reduce((sum, [, v]) => sum + v, 0)
    const mktEntries: Array<{ key: string; count: number }> = [
        ...CORE_KEYS.filter(k => (mkt[k] ?? 0) > 0).map(k => ({ key: k, count: mkt[k] })),
        ...(otherCount > 0 ? [{ key: 'other', count: otherCount }] : []),
    ]

    function handleSearch() {
        if (q.trim()) onSearch(q.trim())
    }

    return (
        <div className="skb__dashboard">
            {/* Stats bar */}
            <div className="skb__stat-bar">
                <StatCard
                    icon={<DatabaseOutlined />}
                    label="收录股票"
                    value={totalStocks.toLocaleString()}
                    sub="个股"
                />
                <StatCard
                    icon={<FileTextOutlined />}
                    label="财报记录"
                    value={totalReports.toLocaleString()}
                    sub="条"
                />
                {mktEntries.map(({ key, count }) => (
                    <StatCard key={key} icon={<span className={`skb__mkt-dot skb__mkt-dot--${key}`} />}
                        label={marketLabel[key] ?? '其他'} value={count} sub="只" />
                ))}
                {usingMock && (
                    <span className="skb__mock-badge">演示数据</span>
                )}
            </div>

            {/* Search */}
            <div className="skb__dash-search-wrap">
                <Input
                    className="skb__dash-search"
                    size="large"
                    placeholder="输入股票名称或代码搜索，如：腾讯 / HK.00700"
                    prefix={<SearchOutlined style={{ color: 'var(--text-3)' }} />}
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    onPressEnter={handleSearch}
                    allowClear
                />
                <Button type="primary" size="large" onClick={handleSearch}>搜索</Button>
            </div>

            {/* Market tiles */}
            <div className="skb__mkt-tiles">
                {mktEntries.map(({ key, count }) => (
                    <MarketTile
                        key={key}
                        market={key}
                        count={count}
                        onClick={() => onMarket(key)}
                    />
                ))}
                <div className="skb__mkt-tile skb__mkt-tile--all" onClick={() => onMarket('all')}>
                    <div className="skb__mkt-tile-name">全部股票</div>
                    <div className="skb__mkt-tile-count">{totalStocks} 只</div>
                    <ArrowRightOutlined className="skb__mkt-tile-arrow" />
                </div>
            </div>

            {/* Recent reports */}
            {stats && stats.recent_reports.length > 0 && (
                <div className="skb__recent-block">
                    <div className="skb__recent-header">
                        <span className="skb__recent-title">最新入库财报</span>
                        <span className="skb__recent-badge">{stats.recent_reports.length} 条</span>
                    </div>
                    {stats.recent_reports.map(r => (
                        <RecentReportRow
                            key={r.id}
                            r={r}
                            onClick={() => navigate(`/stock-kb/${encodeURIComponent(r.stock_code)}`)}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

// ── List view ────────────────────────────────────────────────────

const PAGE_SIZE = 50

function ListView({
    q,
    market,
    onBack,
    navigate,
    usingMockFallback,
}: {
    q: string
    market: string
    onBack: () => void
    navigate: (p: string) => void
    usingMockFallback: boolean
}) {
    const [items, setItems] = useState<StockKBItem[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(0)
    const [loading, setLoading] = useState(true)
    const [usingMock, setUsingMock] = useState(usingMockFallback)
    const columns = getColumns(navigate)

    const fetchPage = useCallback(async (pg: number) => {
        setLoading(true)
        try {
            const data = await getStockKBList({ q, market, page: pg, page_size: PAGE_SIZE })
            if (data.total > 0 || data.items.length > 0) {
                setItems(filterValidItems(data.items))
                setTotal(data.total)
                setUsingMock(false)
            } else {
                // fallback: filter mock
                const filtered = mockAsApiItems.filter(s => {
                    const mq = !q || (s.name ?? '').includes(q) || s.stock_code.toLowerCase().includes(q.toLowerCase())
                    const mm = market === 'all' || !market || s.market === market
                    return mq && mm
                })
                setItems(filtered.slice(pg * PAGE_SIZE, (pg + 1) * PAGE_SIZE))
                setTotal(filtered.length)
                setUsingMock(true)
            }
        } catch {
            const filtered = mockAsApiItems.filter(s => {
                const mq = !q || (s.name ?? '').includes(q) || s.stock_code.toLowerCase().includes(q.toLowerCase())
                const mm = market === 'all' || !market || s.market === market
                return mq && mm
            })
            setItems(filtered.slice(pg * PAGE_SIZE, (pg + 1) * PAGE_SIZE))
            setTotal(filtered.length)
            setUsingMock(true)
        } finally {
            setLoading(false)
        }
    }, [q, market])

    useEffect(() => { setPage(0); fetchPage(0) }, [q, market])

    return (
        <div className="skb__list-view">
            <div className="skb__list-toolbar">
                <Button type="text" size="small" className="skb__back-btn" onClick={onBack}>
                    ← 返回概览
                </Button>
                <span className="skb__list-desc">
                    {market && market !== 'all' ? `${marketLabel[market] ?? market} · ` : ''}
                    {q ? `"${q}" · ` : ''}
                    共 {total} 只
                </span>
                {usingMock && <span className="skb__mock-badge">演示数据</span>}
            </div>

            {loading ? (
                <div style={{ padding: '80px 0', textAlign: 'center' }}><Spin size="large" /></div>
            ) : (
                <>
                    <Table
                        dataSource={items}
                        columns={columns}
                        rowKey="stock_code"
                        pagination={false}
                        size="small"
                        onRow={r => ({ onClick: () => navigate(`/stock-kb/${encodeURIComponent(r.stock_code)}`), style: { cursor: 'pointer' } })}
                    />
                    {total > PAGE_SIZE && (
                        <div className="skb__pagination">
                            <Pagination
                                current={page + 1}
                                pageSize={PAGE_SIZE}
                                total={total}
                                showSizeChanger={false}
                                onChange={p => { setPage(p - 1); fetchPage(p - 1) }}
                            />
                        </div>
                    )}
                    {items.length === 0 && (
                        <div className="skb__empty">暂无匹配股票</div>
                    )}
                </>
            )}
        </div>
    )
}

// ── Main page ────────────────────────────────────────────────────

export default function StockKBPage() {
    const navigate = useNavigate()
    const [stats, setStats] = useState<StockKBStats | null>(null)
    const [statsLoading, setStatsLoading] = useState(true)
    const [statsMock, setStatsMock] = useState(false)

    // list mode state
    const [mode, setMode] = useState<'dashboard' | 'list'>('dashboard')
    const [listQ, setListQ] = useState('')
    const [listMarket, setListMarket] = useState('all')

    const fetchStats = useCallback(async () => {
        setStatsLoading(true)
        try {
            const data = await getStockKBStats()
            if (data.total_stocks > 0) {
                setStats(data)
                setStatsMock(false)
            } else {
                setStats(mockStats)
                setStatsMock(true)
            }
        } catch {
            setStats(mockStats)
            setStatsMock(true)
        } finally {
            setStatsLoading(false)
        }
    }, [])

    useEffect(() => { fetchStats() }, [fetchStats])

    function handleSearch(q: string) {
        setListQ(q)
        setListMarket('all')
        setMode('list')
    }

    function handleMarket(m: string) {
        setListQ('')
        setListMarket(m)
        setMode('list')
    }

    function handleBack() {
        setMode('dashboard')
        setListQ('')
        setListMarket('all')
    }

    return (
        <div className="skb">
            <div className="page-header flex flex-between skb__header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="skb__title">个股知识库</span>
                </div>
                <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    type="text"
                    style={{ color: 'var(--text-3)' }}
                    onClick={fetchStats}
                    loading={statsLoading}
                />
            </div>

            <div className="page-content skb__content">
                {statsLoading ? (
                    <div style={{ padding: '120px 0', textAlign: 'center' }}><Spin size="large" /></div>
                ) : mode === 'dashboard' ? (
                    <Dashboard
                        stats={stats}
                        usingMock={statsMock}
                        onSearch={handleSearch}
                        onMarket={handleMarket}
                        navigate={navigate}
                    />
                ) : (
                    <ListView
                        q={listQ}
                        market={listMarket}
                        onBack={handleBack}
                        navigate={navigate}
                        usingMockFallback={statsMock}
                    />
                )}
            </div>
        </div>
    )
}
